/**
 * API client.
 *
 * Same origin as the API in production, and the Vite dev server proxies `/api`
 * to uvicorn locally — so `credentials: 'same-origin'` is all the session cookie
 * needs, and there is no base URL to configure.
 *
 * **The CSRF token is attached to every state-changing request.** It comes from
 * `/api/auth/me` (or the login response) and lives only in memory: putting it in
 * `localStorage` would make it readable by any script on the page, which is the
 * thing it exists to defend against.
 */

/** `/api/health` since 16 Oct (docs/BETTERSTACK-EXECUTION-PLAN.md §3): public, so pass/warn/fail
 *  per check and nothing else. The deployment facts it used to carry moved behind a token to
 *  `/api/ops/status`. A failing check answers 503, which `request()` throws on. */
export interface Health {
  status: 'pass' | 'warn' | 'fail'
  time: string
  checks: Record<string, 'pass' | 'warn' | 'fail'>
}

/** What an Admin can grant a user. Revenue is not one of them: since RV-3
 *  (25 Sep) revenue is Admin-only, and the server refuses a revenue grant row. */
export type Grant = 'stories' | 'spotlights' | 'channels'

/** Everything a gate can ask for — the grants plus the two Admin-only revenue
 *  names, which mirror the server's `ADMIN_ONLY`. */
export type Permission = Grant | 'revenue_view' | 'revenue_enter'

export const ALL_GRANTS: Grant[] = ['stories', 'spotlights', 'channels']

export const GRANT_LABELS: Record<Grant, string> = {
  stories: 'Stories',
  spotlights: 'Spotlights',
  channels: 'Channel Overview',
}

export interface Me {
  /** `false` when nobody is signed in — `/api/auth/me` answers 200 either way,
   *  because "nobody" is a valid answer to "who am I" and a 401 would make the
   *  browser log a console error on every signed-out page load. */
  authenticated: boolean
  email: string | null
  full_name: string | null
  role: 'admin' | 'user' | null
  grants: Grant[]
  csrf_token: string | null
  /** Which sidebar sections the server says this user may open. */
  sections: string[]
}

/** Channel Daily — the spec's column contract for tables 4 and 7. */
export const DAILY_COLUMNS = [
  'STORY_VIEWS',
  'SPOTLIGHT_VIEWS',
  'VIEWERS',
  'SHARES',
  'REPLIES',
  'SCREENSHOTS',
  'SUBSCRIBERS',
] as const

export type DailyColumn = (typeof DAILY_COLUMNS)[number]

export const DAILY_COLUMN_LABELS: Record<DailyColumn, string> = {
  STORY_VIEWS: 'Story Views',
  SPOTLIGHT_VIEWS: 'Spotlight Views',
  VIEWERS: 'Viewers',
  SHARES: 'Shares',
  REPLIES: 'Replies',
  SCREENSHOTS: 'Screenshots',
  SUBSCRIBERS: 'Subscribers',
}

export interface DailyRow {
  profile_id: string
  handle: string
  display_label: string
  language_group: 'english' | 'international' | null
  stat_date: string
  /** A metric absent for a channel-day is `null`, never `0` — zero is a measurement. */
  metrics: Partial<Record<DailyColumn, number | null>>
}

/** One channel's Snap-lifetime totals — CO-8. No date, because there is none. */
export interface ChannelLifetimeRow {
  profile_id: string
  handle: string
  display_label: string
  language_group: 'english' | 'international' | null
  metrics: Partial<Record<DailyColumn, number | null>>
}

export interface ChannelLifetime {
  /** Which snapshot these came from. Keyed on the run, never a timestamp. */
  capture_run_id: number | null
  captured_at: string | null
  rows: ChannelLifetimeRow[]
  /** From `snap.metric_registry`. Decides which columns may carry a total — the
   *  browser never holds its own copy of the counting-basis rules. */
  aggregable: Partial<Record<DailyColumn, boolean>>
  /** Metrics Snap reports as zero on every channel here, measured per response.
   *  Rendered absent-with-a-reason, never as a column of zeros. */
  withheld: DailyColumn[]
}

export interface SeriesPoint {
  stat_date: string
  story_views: number
  saved_story_views: number
  /** Only on the `english_spotlights` chart (CO-12); null elsewhere. */
  spotlight_views: number | null
}

export interface ChartSeries {
  key: string
  title: string
  /** The channels the line actually covers, after the filter. */
  channels: string[]
  /** How many it would cover unfiltered — so the UI can say "3 of 10". */
  channels_available: number
  /** The shared counting basis, from `snap.metric_registry`. Labels the axis. */
  basis: string
  points: SeriesPoint[]
}

export interface CoverageCell {
  profile_id: string
  stat_date: string
  status: string
}

/** What a column's caveat dot renders — straight from the metric registry. */
export interface MetricNote {
  metric_name: string
  counting_basis: string
  aggregable: boolean
  reliability: string
  caveat: string | null
}

// ---------------------------------------------------------------------------
// Assets — Stories (§4.2) and Spotlights (§4.3)
// ---------------------------------------------------------------------------

/** A metric a window cannot honestly produce is returned null and named here. */
export interface WithheldAware {
  withheld: string[]
}

export interface SpotlightRow {
  asset_id: string
  handle: string
  display_label: string
  language_group: 'english' | 'international' | null
  title: string | null
  published_at: string | null
  duration: number | null
  has_thumbnail: boolean
  moderation_status: string | null
  metrics: Record<string, number | null>
  /** From ClickUp, on the spotlights that matched — CU-3. */
  episode_number: string | null
  author: string | null
  classification: string | null
  match_status: string | null
  candidate_count: number | null
  /** The matched ClickUp task (RD-22). */
  chosen_task_id: string | null
  /** MT-17: `manual` when an Admin pinned this task. */
  match_source: 'automatic' | 'manual' | 'linked' | null
  /** A pin the worker has not applied yet, when it differs from `chosen_task_id`. */
  pinned_task_id: string | null
  /** The matched task is filed under another channel. */
  cross_channel: boolean
}

export interface SpotlightTable extends WithheldAware {
  window: 'lifetime' | 'range'
  rows: SpotlightRow[]
}

export interface DurationBand {
  band: string
  spotlights: number
  median_views: number
}

export interface Kpi {
  metric: string
  value: number | null
  withheld: boolean
  /** Spotlights: `channel` is Snap's channel total and can be compared with the
   *  preceding period; `listed` is added up from the spotlights Snap lists and
   *  never is. Absent elsewhere. */
  basis?: 'channel' | 'listed'
  /** Days a `channel` figure is drawn from — comparisons need both windows equal. */
  days?: number
}

export interface StoryRow {
  asset_id: string
  handle: string
  display_label: string
  language_group: 'english' | 'international' | null
  title: string | null
  published_at: string | null
  /** The matched task's own publish date — a linked saved story's task predates
   *  the post, so Raw Data links are dated from this (RD-22, ST-28). */
  task_publish_date?: string | null
  /** ClickUp's `Duration` on the matched task — 24 Hours · 2 Days · 3 Days · 7 Days
   *  · N/A (ST-27). Null when unmatched or not filled in. */
  air_time: string | null
  /** `air_time` in hours, for sorting. Null for `N/A` and for no value. */
  air_time_hours: number | null
  /** The video's length from the matched ClickUp task, `Runtime` else `TRT` (ST-17). */
  run_time_seconds: number | null
  /** From ClickUp, where the story matched. `match_status` says why these are empty. */
  episode_number: string | null
  author: string | null
  /** `rerun` where the task name says so; **null means unknown, not original**. */
  classification: string | null
  match_status: 'matched' | 'ambiguous' | 'unmatched' | null
  candidate_count: number | null
  /** 0 = ClickUp's date equals Snap's. ±1 = no task was dated that day at all. */
  match_day_offset: number | null
  /** The matched ClickUp task; null unless `match_status` is `matched` (RD-22). */
  chosen_task_id: string | null
  /** MT-17: `manual` when an Admin pinned this task. */
  match_source: 'automatic' | 'manual' | 'linked' | null
  /** A pin the worker has not applied yet, when it differs from `chosen_task_id`. */
  pinned_task_id: string | null
  /** The matched task is filed under another channel. */
  cross_channel: boolean
  has_thumbnail: boolean
  metrics: Record<string, number | null>
}

/** The Viewers tiles. **Never a count of distinct people over a range** —
 *  Snap de-duplicates only within one channel-day, story or channel lifetime. */
export interface ViewerKpis {
  window: 'lifetime' | 'range'
  avg_daily_viewers: number | null
  days: number
  avg_viewers_per_episode: number | null
  episodes: number
  channel_unique_viewers: number | null
}

/** One Channel Overview tile. `days` is how many days the total draws from —
 *  CO-14 compares only windows holding the same number. */
export interface ChannelKpi {
  metric: string
  value: number | null
  days: number
}

/** MT-17 — who else holds a task the Admin is looking at. */
export interface MatchHolder {
  asset_id: string
  asset_type: MatchAssetType
  display_label: string
  published: string | null
  /** `manual` cannot be displaced; `automatic` can, and that post falls back. */
  source: 'automatic' | 'manual'
}

export interface MatchSuggestion {
  task_id: string
  name: string | null
  episode_number: string | null
  author: string | null
  publish_date: string | null
  display_label: string | null
  intended_asset_type: string | null
  day_offset: number | null
  considered: boolean
  cross_channel: boolean
  cross_type: boolean
  held_by: MatchHolder | null
}

export interface MatchSuggestions {
  asset_id: string
  asset_type: MatchAssetType
  display_label: string
  published: string | null
  current_task_id: string | null
  current_source: 'automatic' | 'manual' | 'linked' | null
  pinned_task_id: string | null
  mode: 'suggested' | 'search' | 'id'
  not_found: boolean
  rows: MatchSuggestion[]
}

export interface MatchApplying {
  status: 'queued'
  detail: string
  starts_within_seconds: number
}

/** ST-23 — one story's views and viewers per UTC day. */
export interface StoryDaily {
  asset_id: string
  asset_type: 'STORY' | 'SAVED_STORY' | 'SPOTLIGHT'
  points: { stat_date: string; views: number | null; viewers: number | null }[]
  /** The first collected day, when the post is older than our history. */
  history_starts: string | null
}

/** ST-24 — the Saved Stories KPI row, channel level throughout. */
export interface SavedKpis {
  window: 'lifetime' | 'range'
  totals: Record<
    'SAVED_STORY_VIEWS' | 'SAVED_STORY_VIEW_TIME_MILLIS' | 'SAVED_STORY_FAVORITES' | 'SAVED_STORY_SUBSCRIBES',
    number | null
  >
  avg_daily_viewers: number | null
  channel_unique_viewers: number | null
  days: number
}

export interface StoryTable extends WithheldAware {
  asset_type: 'STORY' | 'SAVED_STORY'
  window: 'lifetime' | 'range'
  /** Shown from Snap's lifetime totals although a range was asked for (ST-19). */
  lifetime_metrics: string[]
  /** Keys in `metrics` holding a uniques metric's daily figures added up over
   *  the range, e.g. `STORY_UNIQUES_DAILY_SUM` (ST-32). Empty under lifetime. */
  daily_sum_metrics?: string[]
  rows: StoryRow[]
}

/** The Q-C9 shortfall, as a number rather than a caveat. */
export interface SavedReconciliation {
  date_from: string
  date_to: string
  per_asset_total: number
  channel_level_total: number
  ratio: number | null
  assets_counted: number
}

export const METRIC_LABELS: Record<string, string> = {
  SPOTLIGHT_VIEWS: 'Views',
  SPOTLIGHT_UNIQUES: 'Viewers',
  SPOTLIGHT_SUBSCRIBES: 'Subscribers Gained',
  SPOTLIGHT_SCREENSHOTS: 'Screenshots',
  SPOTLIGHT_COUNT: 'Spotlights',
  STORY_VIEWS: 'Story Views',
  STORY_UNIQUES: 'Viewers',
  SAVED_STORY_VIEWS: 'Story Views',
  SAVED_STORY_UNIQUES: 'Viewers',
  STORY_COUNT: 'Stories',
  SHARES: 'Shares',
  REPLIES: 'Replies',
  VIEW_TIME_MILLIS: 'View Time',
  AVG_VIEW_TIME_MILLIS: 'Avg View Time',
}

export interface Channel {
  profile_id: string
  handle: string
  /** Ours, not Snap's — `phonynorway`'s Snap name carries a double space. */
  display_label: string
  language_group: 'english' | 'international' | null
  has_own_chart: boolean
  sort_order: number
}

export interface AdminUser {
  id: number
  email: string
  full_name: string | null
  role: 'admin' | 'user'
  status: 'pending' | 'active' | 'disabled'
  email_verified: boolean
  has_password: boolean
  grants: Grant[]
  last_login_at: string | null
  created_at: string
}


// ---------------------------------------------------------------------------
// Revenue — spec §5.1. Storage is long, entry is wide.
// ---------------------------------------------------------------------------

/** `amount: null` clears the cell, which DELETEs the row. **Never a zero.** */
export interface RevenueCell {
  profile_id: string
  revenue_date: string
  amount: string | null
  currency: string | null
  source: string | null
}

export interface RevenueGridChannel {
  profile_id: string
  display_label: string
  language_group: 'english' | 'international' | null
  sort_order: number
  revenue_column: string | null
  /** `confirmed` | `corroborated` | `inferred` | `none` — drives Q-C1's per-column caveat. */
  revenue_confidence: string
}

export interface RevenueGrid {
  date_from: string
  date_to: string
  currency: string
  /** Q-C20 — USD, confirmed by Nilay 25 Sep (RV-8). */
  currency_confirmed: boolean
  date_basis: string
  channels: RevenueGridChannel[]
  /** Only the cells holding a figure. An absent (channel, day) is *not recorded*. */
  cells: RevenueCell[]
  /** A rendering hint. The write endpoints enforce this independently. */
  editable: boolean
  /** The first day anything is recorded — the *All Recorded* range (RV-7). */
  earliest_recorded: string | null
}

export interface RevenueHistoryRow {
  profile_id: string
  display_label: string
  revenue_date: string
  amount: string
  currency: string
  source: string
  entered_by: string | null
  updated_by: string | null
  updated_at: string
}

export interface RevenueAuditRow {
  changed_at: string
  changed_by: string | null
  profile_id: string
  display_label: string | null
  revenue_date: string
  action: 'created' | 'updated' | 'deleted'
  old_amount: string | null
  new_amount: string | null
  currency: string | null
}

export interface RevenueWriteResult {
  created: number
  updated: number
  deleted: number
  unchanged: number
}

export interface RevenueQuery {
  from: string
  to: string
  /** Language-group scope. Absent means the whole estate, never none. */
  group?: 'english' | 'international'
  /** Explicit handles. Empty means every channel — same contract as the
   *  dashboards, where a fresh link carries no `channels` parameter. */
  channels?: string[]
}

function revenueQuery(p: RevenueQuery): string {
  const query = new URLSearchParams({ date_from: p.from, date_to: p.to })
  if (p.group) query.set('group', p.group)
  if (p.channels?.length) query.set('channels', p.channels.join(','))
  return query.toString()
}

// ---------------------------------------------------------------------------
// Phase 8 — Connections, raw data, legacy
// ---------------------------------------------------------------------------

export interface PipelineHealth {
  pipeline: string
  /** `abandoned` is derived on the server from run age, never stored. */
  state:
    | 'succeeded'
    | 'failed'
    | 'partial'
    | 'running'
    | 'abandoned'
    | 'reverted'
    | 'never_run'
  last_success_at: string | null
  last_attempt_at: string | null
  last_attempt_status: string | null
  last_duration_seconds: number | null
  rows_written: number | null
  last_error: string | null
  runs_7d: number
  /** Counted separately, and named accurately: a `partial` run is not a failure,
   *  and neither necessarily means data is missing — the strip is the authority
   *  on coverage, because a later run may have filled the same days. */
  failed_7d: number
  partial_7d: number
  scheduled: boolean
  schedule_note: string | null
  /** How fresh the *data* is, which is not the same as when the run happened —
   *  a run can succeed and return nothing. */
  data_through: string | null
  /** The source-specific coverage line. Snap's is on its strip instead. */
  detail: string | null
  next_run_at: string | null
  triggerable: boolean
  /** `pending` while queued, `claimed` while the worker has it. */
  trigger_state: 'pending' | 'claimed' | null
  trigger_requested_at: string | null
}

export interface RunRow {
  id: number
  pipeline: string
  trigger: string
  status: string
  started_at: string
  finished_at: string | null
  duration_seconds: number | null
  rows_written: number | null
  error: string | null
  /** Who pressed the button. Null for a scheduled run — nobody pressed it. */
  requested_by: string | null
}

export type DayState = 'complete' | 'partial' | 'failed' | 'critical' | 'not_attempted'

export interface StripDay {
  day: string
  state: DayState
  /** The design-system status token, chosen by the server. The client never
   *  maps a state to a colour itself — one mapping, tested once. */
  token: 'good' | 'warning' | 'serious' | 'critical' | 'idle'
  label: string
  covered: number
  failed: number
  claimed: number
  unavailable: number
  expected: number
  channels: number
  rows: number
  /** The **chunk's** fetch time, not the day's — a work unit spans up to 15
   *  days, so the tooltip labels it as the request that included this day. */
  chunk_duration_seconds: number | null
  /** From `app.pipeline_work_attempts`, which is append-only. The units table
   *  overwrites its error on retry and destroys the diagnosis. */
  last_error: string | null
  last_error_http_status: number | null
}

export interface Strip {
  pipeline: string
  /** `run_history` cannot see a per-channel gap, and the page must say so. */
  basis: 'coverage_ledger' | 'run_history'
  basis_note: string
  days: StripDay[]
  complete_days: number
  total_days: number
  /** Complete days **within the contracted history**. The strip is wider than
   *  what anyone asked us to collect, so this is the figure that describes
   *  coverage; `complete_days` describes the strip. */
  complete_in_history: number
  history_days: number
}

export interface CoverageResponse {
  date_from: string
  date_to: string
  earliest_reachable: string
  history_start: string
  days_until_history_lost: number
  expected_units_per_day: number
  core_datasets: string[]
  strips: Strip[]
  headline: string
}

export interface TriggerAccepted {
  request_id: number
  pipeline: string
  scope: 'full' | 'day'
  stat_date: string | null
  /** Worst case before the worker starts it. The web container cannot run a
   *  pipeline, so the button can promise queued, not started. */
  starts_within_seconds: number
  detail: string
}

export interface FailedChannel {
  profile_id: string
  display_label: string
  dataset: string
  status: string
  rows: number
  note: string | null
}

export interface MatchCandidate {
  task_id: string
  name: string | null
  episode_number: string | null
  author: string | null
  classification: string | null
  intended_asset_type: string | null
  publish_date_local: string | null
  day_offset: number
  chosen: boolean
  /** The asset holding this task, when it is not this one. Matching is
   *  one-to-one, so a held task is not an option here however plausible it
   *  looks — and nothing else in the row says so. */
  taken_by_asset_id: string | null
  taken_by_asset_type: MatchAssetType | null
  /** The holder's publish date — usually the task's exact day, which is what
   *  beat this asset's ±1d claim. */
  taken_by_publish_date: string | null
}

export type MatchAssetType = 'STORY' | 'SPOTLIGHT' | 'SAVED_STORY'
export type MatchStatus = 'matched' | 'unmatched' | 'ambiguous' | 'not_tracked'

export interface MatchRow {
  asset_id: string
  asset_type: MatchAssetType
  profile_id: string
  display_label: string
  published_at: string | null
  publish_date_local: string | null
  status: MatchStatus
  candidate_count: number
  /** Candidates still available to this asset. Below `candidate_count` whenever
   *  another asset holds one, which is what makes `2 candidates` misleading on
   *  its own. */
  free_candidate_count: number
  tolerance_days: number | null
  match_tz: string | null
  rule_version: number | null
  evaluated_at: string | null
  has_thumbnail: boolean
  candidates: MatchCandidate[]
  /** `automatic` (channel + date) · `manual` (an Admin's pin) · `linked` (a saved
   *  story given its public story's task, ST-28). */
  match_source: 'automatic' | 'manual' | 'linked' | null
  chosen_task_id: string | null
  pinned_task_id: string | null
  /** The task a matched row got — shown even when it is not one of its own
   *  candidates, as on every linked row. */
  chosen_task: {
    task_id: string
    name: string | null
    episode_number: string | null
    author: string | null
  } | null
  /** ST-28: the public story a saved story was saved from, and the evidence. */
  linked_story: {
    asset_id: string
    published_at: string | null
    snaps_matched: number
    snaps_total: number
  } | null
}

/** One page of the reconciliation list, **with the total for the filter.**
 *
 * 🔴 The endpoint used to return a bare `MatchRow[]` capped at 200 with nothing
 * saying so, and this page then paginated the truncated set — a pager reading
 * `Page 1 of 8` over a list missing 566 rows. `row_count` is the total *before*
 * paging, so the screen can never again disagree with its own header.
 */
export interface MatchPage {
  rows: MatchRow[]
  row_count: number
  page: number
  page_size: number
}

export interface MatchSummary {
  matched: number
  ambiguous: number
  unmatched: number
  /** The channel carries no ClickUp tasks of this type, so there was nothing to
   *  match against. Different from `unmatched`, which means we looked. */
  not_tracked: number
  total: number
  exact_day_matched: number
  drift_matched: number
  /** How the matched rows were matched (ST-28); the three sum to `matched`. */
  matched_by_date: number
  matched_linked: number
  matched_manual: number
  match_tz: string | null
  tolerance_days: number | null
  rule_version: number | null
  last_evaluated_at: string | null
}

/** One filter a dataset answers (RD-18). `choice` offers a list from
 *  `/filter-options`; `assets` is the story picker; `text` takes exact typed
 *  values; `search` matches part of a name, any case (RD-22). */
export interface FilterCard {
  key: string
  label: string
  kind: 'choice' | 'assets' | 'text' | 'search'
}

export interface DatasetCard {
  key: string
  label: string
  description: string
  schema_name: string
  table: string
  date_column: string | null
  columns: string[]
  requires: Permission | null
  caveat: string
  /** Null when fetched without counts — the page's default. */
  row_count: number | null
  filters: FilterCard[]
}

/** Filter key → values, as `/raw-data` sends them. Every filter is multi-value. */
export type RawFilters = Record<string, string[]>

/** ST-27c — one ClickUp task's custom fields, decoded for the Raw Data panel. */
export interface ClickUpTaskFields {
  task_id: string
  name: string | null
  fields: {
    name: string
    /** What ClickUp would show; null when the stored value is empty. */
    value: string | null
    /** The value as stored, JSON-encoded. */
    stored: string
    /** True when `value` was decoded from a code — show `stored` beside it. */
    decoded: boolean
  }[]
}

export interface FilterOption {
  value: string
  label: string
}

/** A story or spotlight the picker found (RD-19). */
export interface AssetHit {
  asset_id: string
  asset_type: 'STORY' | 'SAVED_STORY' | 'SPOTLIGHT'
  handle: string
  display_label: string
  published_at: string | null
  title: string | null
  episode_number: string | null
  author: string | null
}

function withFilters(query: URLSearchParams, filters?: RawFilters) {
  for (const [key, values] of Object.entries(filters ?? {})) {
    if (values.length) query.set(key, values.join(','))
  }
}

export interface DatasetPreview {
  key: string
  columns: string[]
  rows: (string | null)[][]
  truncated: boolean
  row_count: number
  caveat: string
  page: number
  page_size: number
  /** What `sort` may name — from the server's registry, so the client marks
   *  exactly these headers sortable rather than offering a control that 400s. */
  sortable: string[]
  sort: string | null
  direction: 'asc' | 'desc'
}

export interface LegacyScreen {
  key: string
  title: string
  workbook: string
  view: string
  note: string
  requires: Permission | null
}

export interface LegacyScreens {
  screens: LegacyScreen[]
  configured: boolean
  frozen_on: string
  banner: string
  total_screens: number
}

export interface LegacyToken {
  key: string
  src: string
  token: string
  expires_in_seconds: number
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// In memory only — never localStorage. See the module note.
let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body) headers.set('Content-Type', 'application/json')
  if (UNSAFE.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken)

  const response = await fetch(path, { ...init, method, headers, credentials: 'same-origin' })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    throw new ApiError(detailOf(payload) ?? `${method} ${path} failed`, response.status)
  }
  return payload as T
}

/** FastAPI puts the message in `detail`, which is a string or a validation list. */
function detailOf(payload: unknown): string | null {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const first = detail[0] as { msg?: string } | undefined
      if (first?.msg) return first.msg
    }
  }
  return null
}

export interface AssetQuery {
  from: string
  to: string
  window?: 'lifetime' | 'range'
  group?: 'english' | 'international'
  channels?: string[]
}

function assetQuery(p: AssetQuery): string {
  const query = new URLSearchParams({ from: p.from, to: p.to })
  if (p.window) query.set('window', p.window)
  if (p.group) query.set('group', p.group)
  if (p.channels?.length) query.set('channels', p.channels.join(','))
  return query.toString()
}

/** The mirrored thumbnail. Never Snap's CDN URL — those rotate and 404 for good. */
export function thumbnailUrl(assetId: string): string {
  return `/api/assets/${encodeURIComponent(assetId)}/thumbnail`
}

export const api = {
  health: () => request<Health>('/api/health'),

  me: () => request<Me>('/api/auth/me'),

  login: (email: string, password: string) =>
    request<Me>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ status: string }>('/api/auth/logout', { method: 'POST' }),

  register: (email: string, password: string, fullName: string | null) =>
    request<{ detail: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName }),
    }),

  forgotPassword: (email: string) =>
    request<{ detail: string }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ status: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  setPassword: (token: string, password: string) =>
    request<{ status: string }>('/api/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  verifyEmail: (token: string) =>
    request<{ status: string }>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'POST',
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ detail: string }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  channels: () => request<Channel[]>('/api/channels'),

  channelDaily: (params: { from: string; to: string; group?: string; channels?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.group) query.set('group', params.group)
    if (params.channels?.length) query.set('channels', params.channels.join(','))
    return request<DailyRow[]>(`/api/channels/daily?${query}`)
  },

  channelLifetime: (params: { group?: string; channels?: string[] }) => {
    const query = new URLSearchParams()
    if (params.group) query.set('group', params.group)
    if (params.channels?.length) query.set('channels', params.channels.join(','))
    return request<ChannelLifetime>(`/api/channels/lifetime?${query}`)
  },

  channelKpis: (params: { from: string; to: string; group?: string; channels?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.group) query.set('group', params.group)
    if (params.channels?.length) query.set('channels', params.channels.join(','))
    return request<ChannelKpi[]>(`/api/channels/kpis?${query}`)
  },

  channelSeries: (params: { from: string; to: string; group?: string; channels?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.group) query.set('group', params.group)
    if (params.channels?.length) query.set('channels', params.channels.join(','))
    return request<ChartSeries[]>(`/api/channels/series?${query}`)
  },

  channelCoverage: (params: { from: string; to: string; channels?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.channels?.length) query.set('channels', params.channels.join(','))
    return request<CoverageCell[]>(`/api/channels/coverage?${query}`)
  },

  metricNotes: () => request<MetricNote[]>('/api/channels/metric-notes'),

  spotlightAssets: (p: AssetQuery) =>
    request<SpotlightTable>(`/api/spotlights/assets?${assetQuery(p)}`),

  spotlightKpis: (p: AssetQuery) => request<Kpi[]>(`/api/spotlights/kpis?${assetQuery(p)}`),

  durationBands: (p: AssetQuery) =>
    request<DurationBand[]>(`/api/spotlights/duration-bands?${assetQuery(p)}`),

  viewerKpis: (p: { from: string; to: string; window: 'lifetime' | 'range'; channels: string[]; group?: string }) => {
    const query = new URLSearchParams({ window: p.window })
    if (p.window === 'range') {
      query.set('from', p.from)
      query.set('to', p.to)
    }
    if (p.group) query.set('group', p.group)
    if (p.channels.length) query.set('channels', p.channels.join(','))
    return request<ViewerKpis>(`/api/stories/viewer-kpis?${query}`)
  },

  matchSuggestions: (assetId: string, q: string, allChannels: boolean) => {
    const query = new URLSearchParams()
    if (q) query.set('q', q)
    if (allChannels) query.set('all_channels', 'true')
    return request<MatchSuggestions>(
      `/api/matches/${encodeURIComponent(assetId)}/suggestions?${query}`,
    )
  },
  setManualMatch: (assetId: string, task: string) =>
    request<MatchApplying>(`/api/matches/${encodeURIComponent(assetId)}/manual`, {
      method: 'PUT',
      body: JSON.stringify({ task }),
    }),
  removeManualMatch: (assetId: string) =>
    request<MatchApplying>(`/api/matches/${encodeURIComponent(assetId)}/manual`, {
      method: 'DELETE',
    }),

  clickupTaskFields: (taskId: string) =>
    request<ClickUpTaskFields>(`/api/raw-data/clickup-tasks/${encodeURIComponent(taskId)}/fields`),

  storyDaily: (assetId: string) =>
    request<StoryDaily>(`/api/stories/${encodeURIComponent(assetId)}/daily`),
  spotlightDaily: (assetId: string) =>
    request<StoryDaily>(`/api/spotlights/${encodeURIComponent(assetId)}/daily`),

  savedKpis: (p: { from: string; to: string; window: 'lifetime' | 'range'; channels: string[]; group?: string }) => {
    const query = new URLSearchParams({ window: p.window })
    if (p.window === 'range') {
      query.set('from', p.from)
      query.set('to', p.to)
    }
    if (p.group) query.set('group', p.group)
    if (p.channels.length) query.set('channels', p.channels.join(','))
    return request<SavedKpis>(`/api/stories/saved-kpis?${query}`)
  },

  storyAssets: (p: AssetQuery & { assetType: 'STORY' | 'SAVED_STORY' }) =>
    request<StoryTable>(`/api/stories/assets?${assetQuery(p)}&asset_type=${p.assetType}`),


  savedReconciliation: (p: AssetQuery) =>
    request<SavedReconciliation>(`/api/stories/saved-reconciliation?${assetQuery(p)}`),

  revenueGrid: (p: RevenueQuery) => request<RevenueGrid>(`/api/revenue/grid?${revenueQuery(p)}`),

  revenueHistory: (p: RevenueQuery & { profileId?: string }) => {
    const query = new URLSearchParams(revenueQuery(p))
    if (p.profileId) query.set('profile_id', p.profileId)
    return request<RevenueHistoryRow[]>(`/api/revenue/history?${query}`)
  },

  revenueAudit: (p: RevenueQuery) =>
    request<RevenueAuditRow[]>(`/api/revenue/audit?${revenueQuery(p)}`),

  /**
   * One transaction, all or nothing — a half-applied save would leave the client
   * unsure which figures are stored. Send `amount: null` to clear a cell.
   */
  saveRevenue: (cells: { profile_id: string; revenue_date: string; amount: string | null }[]) =>
    request<RevenueWriteResult>('/api/revenue/cells', {
      method: 'PUT',
      body: JSON.stringify({ cells }),
    }),

  /** The export must carry the same filter the page is showing, or a filtered
   *  view silently hands the user the whole estate. */
  /** RV-4 — the entry grid as it looks on screen: dates × channels, with totals. */
  revenueGridExportUrl: (p: { from: string; to: string }) =>
    `/api/revenue/export-grid?${new URLSearchParams({ date_from: p.from, date_to: p.to })}`,

  revenueExportUrl: (p: RevenueQuery & { dataset: 'revenue' | 'revenue_audit' }) => {
    const query = new URLSearchParams(revenueQuery(p))
    query.set('dataset', p.dataset)
    return `/api/revenue/export?${query}`
  },

  pipelineHealth: () => request<PipelineHealth[]>('/api/connections/pipelines'),

  pipelineRuns: (p: { pipeline?: string; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (p.pipeline) query.set('pipeline', p.pipeline)
    if (p.limit) query.set('limit', String(p.limit))
    return request<RunRow[]>(`/api/connections/runs?${query}`)
  },

  /** Deliberately **not** scoped by the page's date filter: the strip is about
   *  what we hold, and clipping it to a 30-day view would hide the days about to
   *  become unrecoverable. The server picks the whole reachable window. */
  coverage: () => request<CoverageResponse>('/api/connections/coverage'),

  coverageDay: (day: string) =>
    request<FailedChannel[]>(`/api/connections/coverage/${encodeURIComponent(day)}`),

  matchSummary: () => request<MatchSummary>('/api/connections/matches/summary'),

  matches: (
    status: 'unresolved' | 'matched' | 'ambiguous' | 'unmatched' | 'not_tracked' | 'all',
    assetType?: MatchAssetType | null,
    p: { page?: number; pageSize?: number } = {},
  ) => {
    const query = new URLSearchParams({ status })
    if (assetType) query.set('asset_type', assetType)
    if (p.page) query.set('page', String(p.page))
    if (p.pageSize) query.set('page_size', String(p.pageSize))
    return request<MatchPage>(`/api/connections/matches?${query}`)
  },

  /** The catalogue, **without row counts** by default — the page reads the
   *  count of the dataset on screen from its preview. Counting all fourteen
   *  was what kept Raw Data blank for seconds on production. */
  datasets: (p: { from?: string; to?: string; counts?: boolean } = {}) => {
    const query = new URLSearchParams()
    if (p.from) query.set('date_from', p.from)
    if (p.to) query.set('date_to', p.to)
    if (!p.counts) query.set('counts', 'false')
    return request<DatasetCard[]>(`/api/raw-data/datasets?${query}`)
  },

  datasetPreview: (
    dataset: string,
    p: {
      from?: string
      to?: string
      page?: number
      pageSize?: number
      sort?: string | null
      direction?: 'asc' | 'desc'
      filters?: RawFilters
    } = {},
  ) => {
    const query = new URLSearchParams({ dataset })
    if (p.from) query.set('date_from', p.from)
    if (p.to) query.set('date_to', p.to)
    withFilters(query, p.filters)
    if (p.page) query.set('page', String(p.page))
    if (p.pageSize) query.set('page_size', String(p.pageSize))
    if (p.sort) query.set('sort', p.sort)
    if (p.direction) query.set('direction', p.direction)
    return request<DatasetPreview>(`/api/raw-data/preview?${query}`)
  },

  /** One workbook per source, one sheet per permitted dataset. A URL rather
   *  than a fetch, for the same reason as the CSV below. */
  datasetExportAllUrl: (
    source: string,
    p: { from?: string; to?: string; filters?: RawFilters } = {},
  ) => {
    const query = new URLSearchParams({ source })
    if (p.from) query.set('date_from', p.from)
    if (p.to) query.set('date_to', p.to)
    // Each sheet takes the filters it answers; the server drops the rest.
    withFilters(query, p.filters)
    return `/api/raw-data/export-all?${query}`
  },

  /** A URL rather than a fetch: the browser downloads it, so the streaming
   *  response never passes through JS. Carries the page's filter, or a filtered
   *  view would silently hand over the whole table. */
  datasetExportUrl: (
    dataset: string,
    p: { from?: string; to?: string; filters?: RawFilters } = {},
  ) => {
    const query = new URLSearchParams({ dataset })
    if (p.from) query.set('date_from', p.from)
    if (p.to) query.set('date_to', p.to)
    withFilters(query, p.filters)
    return `/api/raw-data/export?${query}`
  },

  filterOptions: (dataset: string) =>
    request<Record<string, FilterOption[]>>(
      `/api/raw-data/filter-options?${new URLSearchParams({ dataset })}`,
    ),

  /** The story picker: search by title, episode #, author or ID — or resolve
   *  IDs already in the URL back into labelled chips. */
  assetSearch: (p: { q?: string; ids?: string[] }) => {
    const query = new URLSearchParams()
    if (p.q) query.set('q', p.q)
    if (p.ids?.length) query.set('ids', p.ids.join(','))
    return request<AssetHit[]>(`/api/raw-data/assets?${query}`)
  },

  /** Queue a run. **Admin only, and 202 rather than 200** — the web container
   *  has SELECT-only on `snap.*`, so this writes a request the worker claims. */
  triggerPipeline: (pipeline: string, day?: string) =>
    request<TriggerAccepted>('/api/connections/trigger', {
      method: 'POST',
      body: JSON.stringify(
        day
          ? { pipeline, scope: 'day', stat_date: day }
          : { pipeline, scope: 'full' },
      ),
    }),

  legacyScreens: () => request<LegacyScreens>('/api/legacy/screens'),

  /** POST because it mints a credential — never cacheable, never prefetchable. */
  legacyToken: (key: string) =>
    request<LegacyToken>(`/api/legacy/screens/${encodeURIComponent(key)}/token`, {
      method: 'POST',
    }),

  adminUsers: () => request<AdminUser[]>('/api/admin/users'),

  approveUser: (id: number, permissions: Grant[]) =>
    request<AdminUser>(`/api/admin/users/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ permissions }),
    }),

  rejectUser: (id: number) =>
    request<AdminUser>(`/api/admin/users/${id}/reject`, { method: 'POST' }),

  inviteUser: (email: string, fullName: string | null, role: string, permissions: Grant[]) =>
    request<AdminUser>('/api/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify({ email, full_name: fullName, role, permissions }),
    }),

  setGrants: (id: number, permissions: Grant[]) =>
    request<AdminUser>(`/api/admin/users/${id}/grants`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),

  setRole: (id: number, role: 'admin' | 'user') =>
    request<AdminUser>(`/api/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  setStatus: (id: number, status: 'active' | 'disabled') =>
    request<AdminUser>(`/api/admin/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  resendLink: (id: number) =>
    request<{ kind: string }>(`/api/admin/users/${id}/resend`, { method: 'POST' }),
}
